import store from '@/store';
import ApiHelper from '@/services/ApiHelper';
import { CustomerAddress, DeleteAddress } from '@/models/addressModels';
import { Account, GenerateTokenResponse, LoginReq } from '@/models/account/authModels';
import UserProfile from '@/models/account/userProfile';
import { CreditCard, DeleteCard } from '@/models/PaymentModels';
import LocationManager from '@/utils/locationManager';
import { useGoogleAnalytics } from '@/composables/useGoogleAnalytics';
import { AddRewardPayload, ValidateBasketPayload } from '@/models/account/rewardModels';
import useAuth from '@/services/authService';
import useVault from '@/services/vaultService';
import { getLocalStorage, localStorageEnum, replaceLocalStorage } from '@/services/localStorageService';
import { OrderLogistics } from '@/models/cartModels';
import { errorToast } from './pageService';
const { initializeUnlockMode } = useVault();

export function addressIconType(type: string) {
    switch (type.toLowerCase().trim()) {
        case 'house':
            return 'mdi-home';
        case 'business':
            return 'mdi-domain';
        case 'campus/base':
            return 'mdi-home-city';
        case 'hotel/hospital':
            return 'mdi-hospital-building';
        default:
            return 'mdi-home';
    }
}

export const addAccountAddressPost = (address: CustomerAddress): Promise<{ addressId: string }> => {
    // api knows "0" means it's a new address
    address.addressId = '0';
    return ApiHelper.post(`/adddeliveryaddress`, address);
};
export const editAccountAddressPost = (address: CustomerAddress): Promise<{ addressId: string }> => ApiHelper.post(`/editdeliveryaddress`, address);
export const deleteAddress = (address: DeleteAddress): Promise<any> => ApiHelper.post(`/deletedeliveryaddress`, address);
export const deleteAccount = (customerId: string): Promise<any> => ApiHelper.post(`/deletecustomer`, { customerId: customerId });

export const fetchStoredCards = (customerId: string): Promise<CreditCard[]> => {
    return ApiHelper.get(`/liststoredcards?customerId=${customerId}`)
        .then((res) => Promise.resolve(res.map((card) => new CreditCard(card))))
        .catch((error) => Promise.reject(error));
};
export const addCreditCard = (card: CreditCard): Promise<{ creditCardID: string }> => {
    return ApiHelper.post(`/addstoredcard`, card.accountPayload())
        .then((res) => Promise.resolve(res))
        .catch((error) => Promise.reject(error));
};
export const updateStoredCard = (card: CreditCard): Promise<{ creditCardId: string }> => {
    return ApiHelper.post(`/editstoredcard`, card.accountPayload())
        .then((res) => Promise.resolve(res))
        .catch((error) => Promise.reject(error));
};
export const deleteStoredCard = (card: DeleteCard): Promise<{ creditCardId: string }> => {
    return ApiHelper.post(`/deletestoredcard`, card)
        .then((res) => Promise.resolve(res))
        .catch((error) => Promise.reject(error));
};

/**
 * If a customer does not exist with the given tokenId email, a new customer will be created as a side effect
 * @param getFullProfile number (1 or 0)
 * @returns Account
 */
export const fetchAccountInfo = (getFullProfile = 1): Promise<Account> => {
    const { trackUserId } = useGoogleAnalytics();

    return new Promise((resolve, reject) =>
        ApiHelper.get(`/getprofile?getFullProfile=${getFullProfile}`)
            .then((res) => {
                if (res instanceof Array && res[0] == 'not found') reject();
                if (res?.customerId) trackUserId(res.customerId);
                resolve(new Account(res));
            })
            .catch((err) => reject(err))
    );
};

export const initUserAccountData = async (): Promise<void> => {
    const { getAccessToken, isAuthenticated, clearStorage, getCognitoUserInfo } = useAuth();
    await initializeUnlockMode();

    store.commit('SET_LOGIN_PROCESSING', true);
    const isAuth = await isAuthenticated();
    if (isAuth) {
        const authToken = await getAccessToken();
        if (authToken) {
            try {
                const userInfo = await getCognitoUserInfo();
                if (userInfo) {
                    const accountInfoResponse = await fetchAccountInfo(1);
                    store.commit('SET_ACCOUNT_INFO', accountInfoResponse);

                    const cart = getLocalStorage(localStorageEnum.cart);
                    if (cart && cart.lineItems) {
                        store.dispatch('initCart', cart.lineItems);
                    }
                    store.dispatch('fetchOrderHistory');

                    const orderLogistics = new OrderLogistics(accountInfoResponse.cart.orderDetails?.orderLogistics);

                    const previouslySelectedLocation = store.getters.getSelectedLocation;
                    if (previouslySelectedLocation) {
                        orderLogistics.storeNumber = previouslySelectedLocation.id;
                    }
                    store.dispatch('updateEstimatedTime', accountInfoResponse.cart.orderHeader?.submitResult.promiseTime.date);
                    store.dispatch('initStoreLocation', orderLogistics);
                    store.dispatch('setSavedCards').catch(() => false);
                }
            } catch (error: any) {
                errorToast(error);
            }
        } else {
            await clearStorage();
        }
    } else {
        initGuestUser();
    }
    store.commit('SET_LOGIN_PROCESSING', false);
};

const initGuestUser = () => {
    const cart = getLocalStorage(localStorageEnum.cart);
    if (cart && cart?.lineItems) {
        store.dispatch('initCart', cart?.lineItems);
    }
    let orderDetail: OrderLogistics = getLocalStorage(localStorageEnum.orderLogistics);
    if (!orderDetail) {
        // If no orderDetail exists in localStorage, add it.
        replaceLocalStorage(localStorageEnum.orderLogistics, store.getters.getOrderLogistics, 0.1);
        orderDetail = getLocalStorage(localStorageEnum.orderLogistics);
    }
    store.dispatch('initStoreLocation', orderDetail ? new OrderLogistics(orderDetail) : undefined);
};

export const fetchRewardsInfo = (): Promise<any> => {
    return ApiHelper.get(`/customer/loyalty`);
};

export const addRewardToBasket = (payload: AddRewardPayload): Promise<any> => {
    // Get current coupons from cart to send to backend for validation
    const coupons = store.getters.getValidCoupons || [];

    return ApiHelper.post(`/customer/loyalty/redeem`, {
        ...payload,
        currentCoupons: coupons,
    });
};

export const removeItemsFromBasket = (payload: ReadonlyArray<number>): Promise<any> => {
    return ApiHelper.post(`/customer/loyalty/unredeem`, { discountBasketItemIds: payload });
};

export const getRewardBasketItems = (): Promise<any> => {
    return ApiHelper.get(`/customer/loyalty/basket`);
};

export const validateRewardBasketItems = (payload: ValidateBasketPayload): Promise<any> => {
    return ApiHelper.post(`/customer/loyalty/batch-redemptions?query=true`, payload);
};

export const getRewardHistory = (): Promise<any> => {
    return ApiHelper.get(`/customer/loyalty/history`);
};

export const updateAccountInfo = (request: UserProfile) =>
    new Promise((resolve, reject) => {
        ApiHelper.post(`/manageprofile`, request)
            .then((response) => {
                resolve(response);
            })
            .catch((e) => {
                reject(e);
            });
    });

export const updateCommunicationPreferences = (request) =>
    new Promise((resolve, reject) => {
        ApiHelper.post(`/updatecommunicationpreferences`, request)
            .then((response) => resolve(response))
            .catch((error) => reject(error));
    });

export const loginRequest = (loginRequest: LoginReq): Promise<Account> => {
    return ApiHelper.post(`/login`, loginRequest)
        .then((res) => Promise.resolve(new Account(res)))
        .catch((error) => Promise.reject(error));
};

export const fetchPaymentGatewayToken = async (recaptchaToken: string): Promise<string> => {
    const locationIdUrl = store.getters.getSelectedLocation ? store.getters.getSelectedLocation.id : LocationManager.getInstance().getLocationId();

    let path = `/aeyndockrjsockfieo?locationId=${locationIdUrl}&recaptchaToken=${recaptchaToken}`;

    const isNative = store.getters.isNative;

    if (isNative) {
        const tokenResponse = await generateToken();
        if (tokenResponse) {
            const nativeToken = tokenResponse.token;
            path += `&nativeToken=${nativeToken}`;
        }
    }
    return ApiHelper.get(path)
        .then((res) => Promise.resolve(res))
        .catch((error) => Promise.reject(error));
};

export const verifyEmail = (code: string): Promise<any> => {
    return ApiHelper.get(`/verifyemail?code=${code}`)
        .then((res) => Promise.resolve(res))
        .catch((error) => Promise.reject(error));
};

export interface MigrationResponse {
    success: boolean;
    message: string;
    email?: string;
    signOut?: boolean;
}

export const migrateSocialToEmail = async (email: string): Promise<MigrationResponse> => {
    return ApiHelper.post('/customer/migrate-social-to-email', { newEmail: email })
        .then((res) => Promise.resolve(res))
        .catch((error) => Promise.reject(error));
};

export const generateToken = (): Promise<GenerateTokenResponse> => {
    return ApiHelper.get('/generate-token');
};
